// SuperAdminPage — owner-only console for tenant management + impersonation.
// Only visible to the OWNER_ADMIN_EMAIL account.
import React, { useEffect, useState } from 'react';
import { api } from './api';
import { beginImpersonation } from './impersonation';
import { useAuth } from './AuthContext';
import { useOwnerMode, productTypeHas, DUAL_LICENSED } from './OwnerModeContext';
import { useNavigate, Link } from 'react-router-dom';
import { toast } from '../hooks/use-toast';
import { confirmDialog, promptDialog } from '@/components/dialogs/DialogHost';
import { errorMessage } from './errors';
import {
  Shield, User as UserIcon, Users, Building2, ExternalLink,
  UserCog, UserCheck, Loader2, Globe, Plus, X, DollarSign,
  MoreVertical, LogOut, Link2Off, Ban, RotateCcw, Mail, Settings as SettingsIcon,
} from 'lucide-react';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from '../components/ui/alert-dialog';
import { Dialog, DialogContent, DialogTitle } from '../components/ui/dialog';
import { Checkbox } from '../components/ui/checkbox';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import AccountsOverview from './super/AccountsOverview';
import GhlSyncPanel from './super/GhlSyncPanel';
import AriveSyncPanel from './super/AriveSyncPanel';
import FeedbackList from './super/FeedbackList';
import ImpersonationAudit from './super/ImpersonationAudit';
import TenantSettingsDialog from './super/TenantSettingsDialog';
import TenantUsersDialog from './super/TenantUsersDialog';

const FAMILY_LABELS = {
  deal_links: 'Deal borrower links',
  loan_share_tokens: 'Loan status links',
  re_share_tokens: 'Transaction status links',
  upload_requests: 'Document upload links',
  esign_requests: 'E-sign links',
  fee_worksheet_approvals: 'Fee worksheet links',
  attorney_reviews: 'Attorney review links',
  lead_invites: 'Borrower / realtor invites',
  staff_invitations: 'Staff invitations',
  public_pdfs: 'Public PDF links',
  partner_tokens: 'Partner tokens',
  api_keys: 'API keys',
};

// Product-type presentation. A dual_licensed tenant matches the mortgage AND
// real_estate owner-mode filters (via productTypeHas); the dual_licensed mode
// narrows to dual tenants only.
const PRODUCT_LABELS = { mortgage: 'MORTGAGE', real_estate: 'REAL ESTATE', [DUAL_LICENSED]: 'DUAL LICENSED' };
const PRODUCT_STYLES = {
  mortgage: { background: '#EEF2FF', color: '#070B5F' },
  real_estate: { background: '#ECFDF5', color: '#065F46' },
  [DUAL_LICENSED]: { background: '#FEF3C7', color: '#92400E' },
};
const tenantMatchesMode = (t, ownerMode) =>
  ownerMode === 'auto' || productTypeHas(t.product_type || 'mortgage', ownerMode);

export default function SuperAdminPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const [settingsTenant, setSettingsTenant] = useState(null);
  const [usersTenant, setUsersTenant] = useState(null);
  const [incidentDialog, setIncidentDialog] = useState(null); // { tenant, kind: 'sessions' | 'links' }
  const [incidentIncludeApiKeys, setIncidentIncludeApiKeys] = useState(false);

  // Product filter — respects the top-nav owner_mode toggle so mortgage/real-estate
  // views stay cleanly separated even when browsing tenants. Now driven by the
  // shared context so switching modes in another tab updates this page live.
  const { ownerMode } = useOwnerMode();

  const isOwner = !!user?.is_super_admin; // platform-owner flag (ticket 54), not an email match

  const loadTenants = () => {
    setLoading(true);
    return api.get('/tenants/admin/all')
      .then(({ data }) => setTenants(data.tenants || []))
      .catch((e) => toast({ title: 'Failed to load tenants', description: e.response?.data?.detail || 'Error' }))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (!isOwner) return;
    loadTenants();
  }, [isOwner]);

  if (!isOwner) {
    return (
      <div className="p-10 max-w-lg mx-auto text-center">
        <Shield size={40} className="mx-auto text-slate-300 mb-3" />
        <div className="font-serif text-xl font-bold" style={{ color: '#070B5F' }}>Owner Admin Only</div>
        <div className="text-sm text-slate-500 mt-2">This console is restricted to the software owner account.</div>
      </div>
    );
  }

  // Ticket 205: suspend blocks every seat's logins and keeps all data; ticket
  // 315: the workspace's shared links stop too. There is no delete — the
  // retention purge removes records on schedule.
  const suspendTenant = async (t) => {
    const reason = await promptDialog({
      title: `Suspend ${t.name}?`,
      description:
        "Everyone is blocked from signing in, and the workspace's shared links (status, upload, signing, deal links) stop working. All data is kept. Reactivate any time.",
      placeholder: 'Reason (optional, saved in the audit log)',
      confirmText: 'Suspend',
      allowEmpty: true,
      multiline: true,
    });
    if (reason === null || reason === undefined) return;
    setBusyId(`${t.tenant_id}-suspend`);
    try {
      await api.post(`/super/tenants/${t.tenant_id}/suspend`, { reason: reason.trim() || null });
      toast({ title: `${t.name} suspended` });
      await loadTenants();
    } catch (e) {
      toast({ title: 'Suspend failed', description: errorMessage(e, 'Try again') });
    } finally {
      setBusyId(null);
    }
  };

  const reactivateTenant = async (t) => {
    if (!(await confirmDialog({ title: `Reactivate ${t.name}?`, description: 'Its members can sign in again.', confirmText: 'Reactivate' }))) return;
    setBusyId(`${t.tenant_id}-reactivate`);
    try {
      await api.post(`/super/tenants/${t.tenant_id}/reactivate`);
      toast({ title: `${t.name} reactivated` });
      await loadTenants();
    } catch (e) {
      toast({ title: 'Reactivate failed', description: errorMessage(e, 'Try again') });
    } finally {
      setBusyId(null);
    }
  };

  // Ticket 205: a tenant nobody has joined yet. This emails a person outside the
  // company, so it asks for the address first; the old link stops working.
  const resendOwnerInvite = async (t) => {
    const email = await promptDialog({
      title: `Send the owner invitation for ${t.name}`,
      description: 'This sends a real email. Any earlier owner invitation stops working. Fix the address here if it was wrong.',
      defaultValue: t.owner_invite_email || '',
      placeholder: 'owner@company.com',
      confirmText: 'Send invitation',
      validate: (v) => (/^\S+@\S+\.\S+$/.test(v.trim()) ? null : 'Enter a valid email address'),
    });
    if (!email) return;
    setBusyId(`${t.tenant_id}-invite`);
    try {
      const { data } = await api.post(`/super/tenants/${t.tenant_id}/owner-invite`, { email: email.trim() });
      toast(data.email_sent
        ? { title: 'Invitation sent', description: `Sent to ${data.email}.` }
        : { title: 'Invitation NOT sent', description: data.reason || 'Delivery failed' });
      await loadTenants();
    } catch (e) {
      toast({ title: 'Invitation failed', description: errorMessage(e, 'Try again') });
    } finally {
      setBusyId(null);
    }
  };

  const impersonate = async (tenantId, kind) => {
    setBusyId(`${tenantId}-${kind}`);
    try {
      const path = kind === 'borrower'
        ? `/super/impersonate/borrower/${tenantId}`
        : `/super/impersonate/tenant/${tenantId}`;
      const { data } = await api.post(path);
      // Stash the owner's token so EXIT IMPERSONATION can restore it (ticket 73).
      beginImpersonation({
        accessToken: data.access_token,
        originalEmail: user.email,
        tenant_name: data.tenant.name,
        as_email: data.user.email,
        as_role: data.user.role,
      });
      // Redirect appropriately
      if (kind === 'borrower') {
        window.location.href = '/borrower';
      } else {
        window.location.href = '/portal';
      }
    } catch (e) {
      toast({ title: 'Impersonation failed', description: e.response?.data?.detail || 'Error' });
    } finally {
      setBusyId(null);
    }
  };

  const openIncidentDialog = (tenant, kind) => {
    setIncidentIncludeApiKeys(false);
    setIncidentDialog({ tenant, kind });
  };

  const confirmIncident = async () => {
    if (!incidentDialog) return;
    const { tenant, kind } = incidentDialog;
    setBusyId(`${tenant.tenant_id}-${kind}`);
    try {
      if (kind === 'sessions') {
        const { data } = await api.post(`/super/tenants/${tenant.tenant_id}/incident/kill-sessions`);
        toast({ title: 'Signed out', description: `${data.affected} member(s) of ${tenant.name} signed out everywhere.` });
      } else {
        const { data } = await api.post(`/super/tenants/${tenant.tenant_id}/incident/kill-links`, { include_api_keys: incidentIncludeApiKeys });
        const nonZero = Object.entries(data.families || {})
          .filter(([, n]) => n > 0)
          .map(([k, n]) => `${FAMILY_LABELS[k] || k}: ${n}`)
          .join(', ');
        toast({
          title: 'Links revoked',
          description: `${data.affected} link(s) revoked in ${tenant.name}.${nonZero ? ` ${nonZero}` : ''}`,
        });
      }
    } catch (e) {
      toast({ title: 'Failed', description: e?.response?.data?.detail?.message || e?.response?.data?.detail || 'Error' });
    } finally {
      setBusyId(null);
      setIncidentDialog(null);
    }
  };

  return (
    <div className="p-4 md:p-8 max-w-[1400px] mx-auto" data-testid="super-admin-page">
      <div className="mb-6 flex flex-wrap items-center gap-3">
        <div className="w-10 h-10 shrink-0 rounded-lg flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #DC2626 0%, #7C2D12 100%)' }}>
          <Shield size={20} className="text-white" />
        </div>
        <div className="flex-1 min-w-[12rem]">
          <div className="text-[11px] uppercase tracking-widest font-bold" style={{ color: '#DC2626' }}>Owner Console</div>
          <h1 className="font-serif text-2xl font-bold" style={{ color: '#070B5F' }}>Super Admin — All Tenants</h1>
          <p className="text-sm text-slate-500 mt-0.5">
            Sign in as any tenant admin or preview their borrower portal. Every impersonation is audit-logged.
          </p>
        </div>
        <Link
          to="/portal/super-admin/revenue"
          data-testid="open-revenue-dashboard"
          className="text-[13px] font-semibold px-4 py-2 rounded-lg text-white flex items-center gap-1.5 shrink-0 shadow-md hover:brightness-105"
          style={{ background: 'linear-gradient(135deg, #6BA82E 0%, #8DC63F 100%)' }}
        >
          <DollarSign size={13} /> Revenue &amp; Sales
        </Link>
        <button
          onClick={() => setShowNew(true)}
          data-testid="new-tenant-btn"
          className="text-[13px] font-semibold px-4 py-2 rounded-lg text-white flex items-center gap-1.5 shrink-0 shadow-md hover:brightness-105"
          style={{ background: 'linear-gradient(135deg, #070B5F 0%, #0f1580 100%)' }}
        >
          <Plus size={13} /> New Tenant
        </button>
      </div>

      {/* Ticket 38: revenue KPIs, deals-in-progress ranking, falling-off list. */}
      <AccountsOverview />

      {/* Ticket 122 S5: GHL sync scheduler health + manual sync-now. */}
      <GhlSyncPanel />

      {/* Ticket 134 P1: Arive webhook sync health (read-only). */}
      <AriveSyncPanel />

      {/* Ticket 123: user feedback inbox. */}
      <FeedbackList />

      {/* Ticket 205: who impersonated whom. */}
      <ImpersonationAudit />

      {loading && (
        <div className="text-slate-500 flex items-center gap-2"><Loader2 size={16} className="animate-spin" /> Loading tenants…</div>
      )}

      {!loading && tenants.length === 0 && (
        <div className="rounded-xl border border-dashed border-slate-200 p-10 text-center text-sm text-slate-500 italic">
          No tenants provisioned yet.
        </div>
      )}

      {!loading && tenants.length > 0 && (
        <>
          {ownerMode !== 'auto' && (
            <div className="mb-3 text-[12px] rounded-lg px-3 py-2 flex items-center gap-2" style={PRODUCT_STYLES[ownerMode]}>
              <span className="font-semibold uppercase tracking-widest text-[10px]">Filter Active</span>
              <span>Showing <b>{PRODUCT_LABELS[ownerMode]}</b> tenants only ({tenants.filter(t => tenantMatchesMode(t, ownerMode)).length} of {tenants.length}). Change via the top-nav owner-mode toggle.</span>
            </div>
          )}
        <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
          <div className="grid grid-cols-[minmax(0,1fr)_3rem_2rem] md:grid-cols-[minmax(0,1.6fr)_minmax(0,0.9fr)_minmax(0,0.5fr)_2rem] gap-3 px-5 py-3 border-b border-slate-100 text-[11px] font-bold uppercase tracking-widest text-slate-500">
            <div>Tenant</div>
            <div className="hidden md:block">Product</div>
            <div>Users</div>
            <div className="sr-only">Actions</div>
          </div>
          <div className="divide-y divide-slate-100">
            {tenants
              .filter(t => tenantMatchesMode(t, ownerMode))
              .map((t) => {
              const pt = t.product_type || 'mortgage';
              return (
                <div key={t.tenant_id} className="grid grid-cols-[minmax(0,1fr)_3rem_2rem] md:grid-cols-[minmax(0,1.6fr)_minmax(0,0.9fr)_minmax(0,0.5fr)_2rem] gap-3 px-5 py-3 items-center hover:bg-slate-50" data-testid={`tenant-row-${t.tenant_id}`}>
                  <div className="min-w-0">
                    <div className="font-semibold text-[14px] truncate" style={{ color: '#070B5F' }}>{t.name}</div>
                    <div className="text-[11px] text-slate-500 font-mono truncate">{t.slug} · {t.status || 'active'}
                      {t.suspended_at && (
                        <span className="ml-2 font-sans font-bold uppercase text-[10px] px-1.5 py-0.5 rounded bg-red-100 text-red-700" data-testid={`tenant-suspended-${t.tenant_id}`}>Suspended</span>
                      )}
                    </div>
                    {/* Mobile: product badge rides under the name; the Product column is md+ only. */}
                    <div className="md:hidden mt-1">
                      <span className="text-[10px] font-bold px-1.5 py-0.5 rounded uppercase whitespace-nowrap" style={PRODUCT_STYLES[pt] || PRODUCT_STYLES.mortgage}>
                        {PRODUCT_LABELS[pt] || PRODUCT_LABELS.mortgage}
                      </span>
                    </div>
                  </div>
                  <div className="hidden md:block">
                    <span className="text-[10px] font-bold px-1.5 py-0.5 rounded uppercase whitespace-nowrap" style={PRODUCT_STYLES[pt] || PRODUCT_STYLES.mortgage}>
                      {PRODUCT_LABELS[pt] || PRODUCT_LABELS.mortgage}
                    </span>
                  </div>
                  <div className="text-[13px] text-slate-600 flex items-center gap-1">
                    <Users size={11} /> {t.user_count || 0}
                  </div>
                  <div className="flex justify-end">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button
                          className="w-8 h-8 rounded-md hover:bg-slate-100 flex items-center justify-center text-slate-500"
                          aria-label={`Actions for ${t.name}`}
                          title="Actions"
                          data-testid={`tenant-actions-${t.tenant_id}`}
                        >
                          {busyId?.startsWith(`${t.tenant_id}-`) ? <Loader2 size={15} className="animate-spin" /> : <MoreVertical size={15} />}
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-60">
                        <DropdownMenuItem
                          onClick={() => impersonate(t.tenant_id, 'tenant')}
                          disabled={!!busyId}
                          data-testid={`impersonate-admin-${t.tenant_id}`}
                          className="gap-2"
                        >
                          <UserCog size={14} /> Sign in as admin
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => impersonate(t.tenant_id, 'borrower')}
                          disabled={!!busyId}
                          data-testid={`impersonate-borrower-${t.tenant_id}`}
                          className="gap-2"
                        >
                          <UserCheck size={14} /> Preview borrower portal
                        </DropdownMenuItem>
                        <DropdownMenuItem asChild className="gap-2">
                          <a href={t.published ? `/site/${t.slug}` : `/site/${t.slug}?preview=1`} target="_blank" rel="noreferrer" data-testid={`tenant-public-${t.tenant_id}`}>
                            <Globe size={14} /> {t.published ? 'View public site' : 'Preview draft site'} <ExternalLink size={11} className="ml-auto text-slate-400" />
                          </a>
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => setSettingsTenant(t)}
                          data-testid={`tenant-settings-${t.tenant_id}`}
                          className="gap-2"
                        >
                          <SettingsIcon size={14} /> Settings
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => setUsersTenant(t)}
                          data-testid={`tenant-users-${t.tenant_id}`}
                          className="gap-2"
                        >
                          <Users size={14} /> Manage users
                        </DropdownMenuItem>
                        {t.user_count === 0 && (
                          <DropdownMenuItem
                            onClick={() => resendOwnerInvite(t)}
                            disabled={!!busyId}
                            data-testid={`tenant-resend-invite-${t.tenant_id}`}
                            className="gap-2"
                          >
                            <Mail size={14} /> Re-send owner invite
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          onClick={() => openIncidentDialog(t, 'sessions')}
                          disabled={!!busyId}
                          data-testid={`super-kill-sessions-${t.tenant_id}`}
                          className="gap-2 text-red-700 focus:text-red-700 focus:bg-red-50"
                        >
                          <LogOut size={14} /> Kill sessions
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => openIncidentDialog(t, 'links')}
                          disabled={!!busyId}
                          data-testid={`super-kill-links-${t.tenant_id}`}
                          className="gap-2 text-red-700 focus:text-red-700 focus:bg-red-50"
                        >
                          <Link2Off size={14} /> Revoke links
                        </DropdownMenuItem>
                        {t.suspended_at ? (
                          <DropdownMenuItem
                            onClick={() => reactivateTenant(t)}
                            disabled={!!busyId}
                            data-testid={`tenant-reactivate-${t.tenant_id}`}
                            className="gap-2"
                          >
                            <RotateCcw size={14} /> Reactivate workspace
                          </DropdownMenuItem>
                        ) : (
                          <DropdownMenuItem
                            onClick={() => suspendTenant(t)}
                            disabled={!!busyId}
                            data-testid={`tenant-suspend-${t.tenant_id}`}
                            className="gap-2 text-red-700 focus:text-red-700 focus:bg-red-50"
                          >
                            <Ban size={14} /> Suspend workspace
                          </DropdownMenuItem>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
        </>
      )}

      <div className="mt-8 rounded-xl p-4" style={{ background: 'rgba(220,38,38,0.06)', border: '1px solid rgba(220,38,38,0.2)' }}>
        <div className="text-[11px] uppercase tracking-widest font-bold flex items-center gap-1.5" style={{ color: '#7f1d1d' }}>
          <Shield size={11} /> Security note
        </div>
        <div className="text-[12px] text-slate-700 mt-1 leading-relaxed">
          Impersonation tokens expire after <b>2 hours</b>. Every impersonation event is written to the <code>impersonation_audit</code>
          collection with timestamp, tenant, and your account. Click <b>Exit Impersonation</b> in the red banner at any time to return to your owner session.
          Kill sessions / Revoke links are written to the audit log as <code>incident.kill_sessions</code> / <code>incident.kill_links</code> with the affected count; they never touch your own session.
        </div>
      </div>

      {settingsTenant && (
        <TenantSettingsDialog
          tenant={settingsTenant}
          onClose={() => setSettingsTenant(null)}
          onSaved={() => { setSettingsTenant(null); loadTenants(); }}
        />
      )}

      {usersTenant && <TenantUsersDialog tenant={usersTenant} onClose={() => { setUsersTenant(null); loadTenants(); }} />}

      {showNew && <NewTenantModal onClose={() => setShowNew(false)} onCreated={() => { setShowNew(false); loadTenants(); }} />}

      <AlertDialog open={!!incidentDialog} onOpenChange={(open) => { if (!open) setIncidentDialog(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {incidentDialog?.kind === 'sessions'
                ? `Sign everyone in ${incidentDialog?.tenant?.name} out?`
                : `Revoke all links in ${incidentDialog?.tenant?.name}?`}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {incidentDialog?.kind === 'sessions'
                ? `Every member of ${incidentDialog?.tenant?.name} will be signed out of every device. This cannot be undone.`
                : `Every outstanding shared link in ${incidentDialog?.tenant?.name} will stop working immediately${incidentIncludeApiKeys ? ', including all API keys' : ' (API keys are not included)'}. This cannot be undone.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          {incidentDialog?.kind === 'links' && (
            <div className="flex items-center gap-2">
              <Checkbox
                checked={incidentIncludeApiKeys}
                onCheckedChange={(v) => setIncidentIncludeApiKeys(!!v)}
                data-testid="super-include-api-keys"
                id="super-include-api-keys"
              />
              <label htmlFor="super-include-api-keys" className="text-sm text-slate-600">Also revoke all API keys</label>
            </div>
          )}
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="super-incident-confirm" onClick={confirmIncident}>
              {incidentDialog?.kind === 'sessions' ? 'Kill sessions' : 'Revoke links'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// ============================================================================
// NewTenantModal — one-click brokerage provisioning form
// Wraps POST /api/tenants/admin/create + auto-suggests slug from name
// ============================================================================
function NewTenantModal({ onClose, onCreated }) {
  // Pre-select product_type based on current owner-mode so mortgage-side flows
  // create mortgage tenants by default, RE-side create RE tenants.
  const { ownerMode } = useOwnerMode();
  const defaultProduct = ownerMode === 'auto' ? 'mortgage' : ownerMode;
  const [form, setForm] = useState({
    name: '',
    slug: '',
    product_type: defaultProduct,
    template: 'modern',
    subscription_tier: 'pro',
    admin_name: '',
    admin_email: '',
  });
  const [saving, setSaving] = useState(false);
  const [slugTouched, setSlugTouched] = useState(false);

  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Auto-derive slug from name unless user has manually edited it
  const onNameChange = (v) => {
    setForm((f) => ({
      ...f,
      name: v,
      slug: slugTouched
        ? f.slug
        : v.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, ''),
    }));
  };

  const canSubmit =
    form.name.trim() && form.slug && form.admin_name.trim() && /^\S+@\S+\.\S+$/.test(form.admin_email.trim());

  const submit = async () => {
    if (!canSubmit) {
      toast({ title: 'Missing fields', description: 'Name, slug, and the owner\'s name and email are required.' });
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post('/tenants/admin/create', {
        ...form,
        name: form.name.trim(),
        admin_name: form.admin_name.trim(),
        admin_email: form.admin_email.trim(),
      });
      if (data.invitation?.email_sent === false) {
        toast({
          title: `${form.name} created — invitation email NOT sent`,
          description: `${data.invitation.reason || 'Delivery failed'}. Use "Re-send owner invite" on the tenant row.`,
        });
      } else {
        toast({
          title: 'Tenant created',
          description: `${form.name} (${data.slug}) — invitation sent to ${form.admin_email.trim()}`,
        });
      }
      onCreated();
    } catch (e) {
      toast({ title: 'Create failed', description: errorMessage(e, 'Try again') });
    } finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent
        showClose={false}
        className="max-w-lg max-h-[90vh] overflow-y-auto p-0 gap-0"
        data-testid="new-tenant-modal"
      >
        {/* Header */}
        <div className="px-6 pt-5 pb-3 border-b border-slate-100 flex items-start justify-between sticky top-0 bg-white">
          <div>
            <div className="text-[11px] uppercase tracking-widest font-bold" style={{ color: '#6BA82E' }}>Owner Only</div>
            <DialogTitle className="font-serif text-xl font-bold" style={{ color: '#070B5F' }}>New Brokerage / Tenant</DialogTitle>
            <div className="text-[12px] text-slate-500 mt-0.5">Creates the tenant and emails its first owner an invitation.</div>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-slate-100 rounded-md"><X size={16} className="text-slate-500" /></button>
        </div>

        <div className="px-6 py-5 space-y-4">
          {/* Brand */}
          <div>
            <label className="text-[11px] uppercase tracking-widest font-bold text-slate-500">Brokerage Name</label>
            <Input
              type="text"
              value={form.name}
              onChange={(e) => onNameChange(e.target.value)}
              placeholder="Acme Realty"
              data-testid="nt-name"
              className="mt-1 h-10 text-[13px] md:text-[13px]"
            />
          </div>

          <div>
            <label className="text-[11px] uppercase tracking-widest font-bold text-slate-500">
              URL Slug <span className="normal-case text-slate-400 font-normal">— appears at /site/{form.slug || 'your-slug'}</span>
            </label>
            <Input
              type="text"
              value={form.slug}
              onChange={(e) => { setSlugTouched(true); upd('slug', e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '')); }}
              placeholder="acme-realty"
              data-testid="nt-slug"
              className="mt-1 h-10 text-[13px] md:text-[13px] font-mono"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[11px] uppercase tracking-widest font-bold text-slate-500">Product</label>
              <Select value={form.product_type} onValueChange={(v) => upd('product_type', v)}>
                <SelectTrigger data-testid="nt-product" className="mt-1 h-10 w-full bg-white text-[13px]"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="mortgage">Mortgage</SelectItem>
                  <SelectItem value="real_estate">Real Estate</SelectItem>
                  <SelectItem value="dual_licensed">Dual Licensed (Mortgage + Real Estate)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-widest font-bold text-slate-500">Subscription Tier</label>
              <Select value={form.subscription_tier} onValueChange={(v) => upd('subscription_tier', v)}>
                <SelectTrigger data-testid="nt-tier" className="mt-1 h-10 w-full bg-white text-[13px]"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="starter">Starter</SelectItem>
                  <SelectItem value="pro">Pro</SelectItem>
                  <SelectItem value="team">Team</SelectItem>
                  <SelectItem value="enterprise">Enterprise</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Divider */}
          <div className="border-t border-slate-100 pt-4">
            <div className="text-[11px] uppercase tracking-widest font-bold mb-3" style={{ color: '#6BA82E' }}>First Owner</div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[11px] uppercase tracking-widest font-bold text-slate-500">Full Name</label>
                <Input
                  type="text"
                  value={form.admin_name}
                  onChange={(e) => upd('admin_name', e.target.value)}
                  placeholder="Jane Doe"
                  data-testid="nt-admin-name"
                  className="mt-1 h-10 text-[13px] md:text-[13px]"
                />
              </div>
              <div>
                <label className="text-[11px] uppercase tracking-widest font-bold text-slate-500">Owner Email</label>
                <Input
                  type="email"
                  value={form.admin_email}
                  onChange={(e) => upd('admin_email', e.target.value)}
                  placeholder="jane@acmerealty.com"
                  data-testid="nt-admin-email"
                  className="mt-1 h-10 text-[13px] md:text-[13px]"
                />
              </div>
            </div>

            <div className="mt-3 text-[11px] text-slate-500" data-testid="nt-invite-note">
              We email the owner an invitation. They set their own password from the link (valid 7 days);
              no password is created or shown here.
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-slate-100 flex items-center justify-end gap-2 sticky bottom-0 bg-white">
          <button onClick={onClose} className="text-[13px] px-4 py-2 rounded-lg text-slate-500 hover:bg-slate-100">Cancel</button>
          <button
            onClick={submit}
            disabled={saving || !canSubmit}
            data-testid="nt-submit"
            className="text-[13px] font-semibold px-4 py-2 rounded-lg text-white flex items-center gap-1.5 disabled:opacity-50"
            style={{ background: 'linear-gradient(135deg, #070B5F 0%, #0f1580 100%)' }}
          >
            {saving ? <Loader2 size={13} className="animate-spin" /> : <Plus size={13} />}
            {saving ? 'Provisioning…' : 'Create & Send Invite'}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
